import type {
  DropdownChangeFn,
  DropdownFilterCallback,
} from '../../../components/Dropdown/Dropdown';
import type {
  Media,
  MediaHydrated,
  RelationWithoutHydration,
} from '../../../lib/flotiq-client/types/api-response';

import { ComboboxOption } from '@headlessui/react';
import { use, useCallback, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import {
  batchPostContentObject,
  getContentObjects,
} from '../../../lib/flotiq-client';
import { getTagsFromQuery } from '../../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../../lib/flotiq-client/response-errors';
import { getTagUrl, getTestProps } from '../../../lib/helpers';
import { RolePermissions } from '../../../lib/rolePermissions';

import CancelButton from '../../../components/Button/predefined/CancelButton/CancelButton';
import SaveButton from '../../../components/Button/predefined/SaveButton/SaveButton';
import Dropdown from '../../../components/Dropdown/Dropdown';

import { useMediaTags } from '../../../hooks/api';
import useSelectedSpace from '../../../hooks/useSelectedSpace';
import useToken from '../../../hooks/useToken';

import { ModalInstanceContext } from '../../../contexts/ModalContext';
import UserContext from '../../../contexts/UserContext';

interface TagsManagementModalProps {
  /**
   * Test id for MediaManagementModal
   */
  testId?: string;
  /**
   * Array with selected media data
   */
  media: (Media | MediaHydrated)[];
  /**
   * Handle tag update callback
   */
  handleTagUpdate?: (
    media: (Media | MediaHydrated)[],
    tags: RelationWithoutHydration[],
  ) => Promise<void>;
}

const TagsManagementModal = ({
  testId,
  media,
  handleTagUpdate,
}: TagsManagementModalProps) => {
  const { t } = useTranslation();
  const jwt = useToken();
  const { space } = useSelectedSpace();
  const modalInstance = use(ModalInstanceContext);
  const { permissions } = use(UserContext);

  const [tagNameError, setTagNameError] = useState('');

  const canCreate = permissions.canCo(
    '_tag',
    RolePermissions.PERMISSIONS_TYPES.CREATE,
  );

  const tagsParams = useMemo(() => {
    return {
      page: 1,
      limit: 20,
      order_by: 'name',
    };
  }, []);

  const { data: tagData, isLoading } = useMediaTags(tagsParams);
  const tagsData = useMemo(() => {
    return tagData.map((element) => ({
      value: element.id,
      label: element.name,
    }));
  }, [tagData]);

  const mediaTags = useMemo(() => {
    return media?.length === 1 && media[0]?.tags
      ? media[0]?.tags?.filter((tag) => 'id' in tag)?.map((tag) => tag.id)
      : [];
  }, [media]);

  const extraOptions = useMemo(() => {
    return media?.length === 1 && media[0]?.tags
      ? media[0]?.tags
          ?.filter((tag) => 'id' in tag)
          ?.map((tag) => ({
            value: tag.id,
            label: tag.name,
          }))
      : [];
  }, [media]);

  const [selectedTags, setSelectedTags] = useState(mediaTags || []);
  const [isSavingTags, setIsSavingTags] = useState(false);

  const createTagsIfNeeded = useCallback(
    async (selectedTags: string[]) => {
      if (!selectedTags.length) return;

      const { body, status, ok } = await getContentObjects(jwt, space, {
        limit: 1000,
        contentTypeName: '_tag',
        ids: selectedTags.map((element) => element),
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }

      if (body.data.length < selectedTags.length) {
        const batchResponse = await batchPostContentObject(
          jwt,
          space,
          '_tag',
          selectedTags
            .filter((tagId) => !body.data.find(({ id }) => tagId === id))
            .map((name) => ({ id: name, name })),
        );
        if (!batchResponse.ok) {
          throw parseResponseErrors(batchResponse.body, batchResponse.status);
        }
      }
    },
    [jwt, space],
  );

  const updateTags = useCallback(async () => {
    setIsSavingTags(true);

    try {
      await createTagsIfNeeded(selectedTags);

      const newTags = selectedTags.map((element) => ({
        dataUrl: getTagUrl(element),
        type: 'internal',
      }));

      await handleTagUpdate?.(media, newTags);
      modalInstance.resolve?.();
    } catch (error) {
      toast.error(
        error instanceof ResponseError
          ? t('TagsManagement.UpdateError')
          : t('Form.CommunicationErrorMessage'),
      );
    } finally {
      setIsSavingTags(false);
    }
  }, [
    createTagsIfNeeded,
    handleTagUpdate,
    media,
    modalInstance,
    selectedTags,
    t,
  ]);

  const handleTagCreateOption = useCallback(
    (query: string) => {
      if (!canCreate) return;

      if (query.match(/^\w+$/)) {
        return (
          <ComboboxOption
            as="li"
            className="relative cursor-default select-none py-2 pl-4 hover:bg-blue-300"
            value={query}
            key={'createTag'}
            {...getTestProps(testId, 'create-tag-option')}
          >
            {t('TagsManagement.CreateTag', { query })}
          </ComboboxOption>
        );
      }
    },
    [canCreate, t, testId],
  );

  const customFilters = useCallback<DropdownFilterCallback>(
    async (query, _, setIsLoading) => {
      if (!query.match(/^\w+$/)) {
        setTagNameError(t('TagsManagement.TagInvalidName'));
        return [];
      }
      setTagNameError('');
      setIsLoading(true);
      const newOptions = await getTagsFromQuery(
        jwt,
        space,
        query,
        t,
        tagsParams,
      );
      setIsLoading(false);
      return newOptions.map((element) => ({
        value: element.id,
        label: element.name,
      }));
    },
    [jwt, space, t, tagsParams],
  );

  const onTagsChange = useCallback<DropdownChangeFn>((event) => {
    setSelectedTags(event.target.value);
  }, []);

  const onTagsBlur = useCallback(() => setTagNameError(''), []);

  return (
    <div className="pb-28" {...getTestProps(testId, `container`)}>
      <Dropdown
        placeholder={t('TagsManagement.SelectNewTags')}
        options={tagsData}
        emptyOptions={<div className="py-2 px-4">{t('Media.EmptyTags')}</div>}
        value={selectedTags}
        onChange={onTagsChange}
        onBlur={onTagsBlur}
        renderEmpty={handleTagCreateOption}
        renderOnEmptyMatch={handleTagCreateOption}
        additionalOptionsClasses="py-1.5! "
        debounceTime={500}
        nullable
        multiple
        ignoreNotFound
        isDataLoading={isLoading}
        filterCallback={customFilters}
        error={tagNameError}
        extraOptions={extraOptions}
        {...getTestProps(testId, `add-tag`, 'testId')}
      />

      <div
        className="w-full absolute left-0 bottom-0 flex items-center justify-center p-3 space-x-5
        border-t border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-900 rounded-b-lg"
      >
        <CancelButton
          onClick={() => modalInstance.resolve?.(null)}
          disabled={isSavingTags}
        />
        <SaveButton
          onClick={updateTags}
          isSaving={isSavingTags}
          label={t('Global.Save')}
          {...getTestProps(testId, 'save', 'testId')}
        />
      </div>
    </div>
  );
};

export default TagsManagementModal;
